import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  useDocumentQuery,
  useFiltersQuery,
  useRemoveDocumentMutation,
  useSessionQuery,
  useUpdateDocumentMutation,
} from "@/lib/queries";
import { ApiError, openDocumentSource } from "@/lib/api";
import {
  normalizeDetailedSummary,
  normalizeExecSummary,
  toProseBlocks,
} from "@/lib/summaries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntityBox } from "@/components/library/EntityBox";
import { EditDocument } from "@/components/library/EditDocument";
import { PdfPage } from "@/components/library/PdfPage";
import { Markdown } from "@/components/ui/markdown";
import { useDocumentPdf } from "@/lib/pdf";
import { ShieldAlert } from "lucide-react";
import { Page, PageHeader, PageScroll } from "@/components/layout/Page";

/**
 * Free-text prose, rendered as blocks.
 *
 * The path an imported EAB document takes: ERIKA stored its summaries as
 * markdown `text` and this schema's column is `jsonb`, so a straight copy
 * arrives as a JSON string. See lib/summaries.ts and PRD §5.3.
 */
function Prose({ text }: { text: string }) {
  return (
    <div className="space-y-3">
      {toProseBlocks(text).map((b, i) => {
        if (b.kind === "heading") {
          return (
            <h3 key={i} className="erika-serif text-lg">
              {b.text}
            </h3>
          );
        }
        if (b.kind === "bullets") {
          return (
            <ul key={i} className="list-disc space-y-1 pl-5 text-sm">
              {b.items?.map((item, j) => <li key={j}>{item}</li>)}
            </ul>
          );
        }
        return (
          <p key={i} className="text-sm leading-relaxed">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

export const Route = createFileRoute("/library/$documentId")({
  // A citation links here with ?page=7, so the page it cited is the one that
  // scrolls into view. Without it a citation lands at the top of a 200-page
  // document, which is barely better than not linking at all.
  validateSearch: (search: Record<string, unknown>): { page?: number } => {
    const page = Number(search.page);
    return Number.isFinite(page) && page > 0 ? { page } : {};
  },
  component: DocumentDetailPage,
});

function DocumentDetailPage() {
  const { documentId } = Route.useParams();
  const { page: targetPage } = Route.useSearch();
  const navigate = useNavigate();
  const detail = useDocumentQuery(documentId);
  const session = useSessionQuery();
  const filters = useFiltersQuery();
  const update = useUpdateDocumentMutation(documentId);
  const removeDocument = useRemoveDocumentMutation();
  const [editing, setEditing] = useState(false);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Requested alongside the detail rather than after it: the PDF is the
  // slow part (it comes from Box), so it starts downloading straight away.
  const pdf = useDocumentPdf(documentId);

  // A manager corrects the catalogue; a member reads it. The server enforces
  // the same line — this only decides whether the control is offered.
  const role = session.data?.user.role;
  const canEdit = role === "manager" || role === "admin" || role === "owner";
  // Removing a document is the administrator's, not the librarian's — the
  // server draws the same line, and offering the button to a manager would
  // only produce a 403.
  const canRemove = role === "admin" || role === "owner";

  const details = detail.data;
  const pages = details?.pages ?? [];

  // Land on the linked page, and stay on it until the reader takes over.
  //
  // One scroll is not enough. It happens when the page text arrives, while
  // every PDF tile is still a letter-sized placeholder; the PDF comes later,
  // and as tiles take their real shape the rows around the target change
  // height under it. A slide deck's rows shrink by half, and a single scroll
  // left the reader looking at the page after the one they followed a link
  // to. So the target is re-aligned whenever the list changes size, until
  // the reader scrolls, clicks or types. Aligned to the top, under the
  // sticky caption, rather than centred: a page taller than the screen,
  // centred, hides its own heading.
  useEffect(() => {
    if (!targetPage || pages.length === 0) return;
    const el = pageRefs.current[targetPage];
    const list = el?.parentElement;
    if (!el || !list) return;
    const align = () => el.scrollIntoView({ block: "start" });
    align();
    const ro = new ResizeObserver(align);
    ro.observe(list);
    const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    const stop = () => {
      ro.disconnect();
      window.clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, stop, true);
    };
    for (const e of events) window.addEventListener(e, stop, { capture: true, passive: true });
    // A PDF from Box can take a while; nothing it does after this is worth
    // moving a reader who has simply not touched anything yet.
    const timer = window.setTimeout(stop, 30_000);
    return stop;
  }, [targetPage, pages.length]);

  // Every page's proportions, before each one is drawn. Tiles start as letter
  // pages and take their own shape only when rendered, so a slide deck's
  // undrawn tiles were twice the height of its drawn ones and the list kept
  // moving as the reader scrolled. Page 1 speaks for the document.
  const [pdfAspect, setPdfAspect] = useState<number | undefined>();
  useEffect(() => {
    if (!pdf.data) return;
    let live = true;
    pdf.data
      .getPage(1)
      .then((p) => {
        const v = p.getViewport({ scale: 1 });
        if (live) setPdfAspect(v.height / v.width);
      })
      .catch(() => {
        /* the tiles keep the letter-page default */
      });
    return () => {
      live = false;
    };
  }, [pdf.data]);

  const back = (
    <Link
      to="/library"
      className="text-xs text-chrome/60 underline-offset-2 transition-colors duration-100 ease-snap hover:text-chrome hover:underline"
    >
      ← Library
    </Link>
  );

  if (detail.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow={back} title="Loading…" />
      </Page>
    );
  }
  if (detail.isError || !details) {
    return (
      <Page>
        <PageHeader
          eyebrow={back}
          title="Not available"
          description="This document does not exist in your library."
        />
      </Page>
    );
  }

  const doc = details.document;

  // Reset is only meaningful when the extracted set exists and differs from
  // what is on the document now. Order matters for neither, so they are
  // compared as sorted sets rather than as arrays.
  const extracted = details.extractedTopics ?? [];
  const canResetTags =
    extracted.length > 0 &&
    [...extracted].sort().join("\u0000") !== [...doc.tags].sort().join("\u0000");

  // Audience is free text from the processor and from the edit form, so the
  // comparison is forgiving about case and stray spaces.
  const internal = doc.audience?.trim().toLowerCase() === "internal";

  const exec = normalizeExecSummary(details.execSummary);
  const detailed = normalizeDetailedSummary(details.detailedSummary);

  return (
    <Page>
      <PageHeader
        eyebrow={back}
        title={
          <>
            {doc.title}
            {doc.docType && (
              <span className="ml-2 align-middle text-xs text-chrome/50">[{doc.docType}]</span>
            )}
          </>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {doc.year && <span>{doc.year}</span>}
            <span className="capitalize">{doc.audience}</span>
            {doc.pages !== null && <span>{doc.pages} pages indexed</span>}
            {doc.status !== "ready" && (
              <Badge tone={doc.status === "failed" ? "gray" : "amber"}>
                {doc.status === "failed" ? "Indexing failed" : "Indexing"}
              </Badge>
            )}
          </span>
        }
        actions={
          <>
          {canEdit && !editing && (
            <Button
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={() => setEditing(true)}
            >
              Edit details
            </Button>
          )}
          {/* Only when there is something to revert to. Offering an action
              that would change nothing is worse than not offering it, and a
              document nobody has corrected is that case. */}
          {canEdit && canResetTags && (
            <Button
              variant="secondary"
              size="sm"
              className="text-xs"
              disabled={update.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Reset this document's topics to what the processor extracted?\n\n` +
                      `Current: ${doc.tags.join(", ") || "none"}\n` +
                      `Extracted: ${(details.extractedTopics ?? []).join(", ") || "none"}`,
                  )
                ) {
                  update.mutate({ resetTopics: true });
                }
              }}
            >
              Reset tags
            </Button>
          )}
          {/* ERIKA offered Remove from the detail page as well as the list
              row, and it belongs here: this is where someone has just read
              enough of a document to decide it does not belong. */}
          {canRemove && (
            <Button
              variant="secondary"
              size="sm"
              className="text-xs text-danger-text"
              disabled={removeDocument.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Remove "${doc.title}" from the library? Its vectors are deleted ` +
                      `immediately, so it will stop appearing in answers.`,
                  )
                ) {
                  removeDocument.mutate(doc.id, {
                    onSuccess: () => void navigate({ to: "/library" }),
                  });
                }
              }}
            >
              Remove
            </Button>
          )}
          {/* The browser downloads straight from storage through a short-lived
              signed URL, rather than streaming a large PDF back through the
              app. The URL is fetched with the session token first, because a
              plain link to the API sends none. */}
          {details.sourceAvailable && (
            <Button
              variant="primary"
              size="sm"
              className="text-xs"
              onClick={() => void openDocumentSource(doc.id)}
            >
              Download original ↓
            </Button>
          )}
          {/* Where the original actually lives, when this deployment holds no
              copy of it. EAB's corpus is in Box and on eab.com and stays
              there, so these are the links the library offers instead of a
              download. rel="noreferrer" because an external destination has no
              business knowing which document page sent the reader. */}
          {(details.links ?? []).map((link) => (
            <Button
              key={link.url}
              asChild
              variant="secondary"
              size="sm"
              className="text-xs"
            >
              <a href={link.url} target="_blank" rel="noreferrer noopener">
                {link.label} ↗
              </a>
            </Button>
          ))}
          </>
        }
      />

      <PageScroll className="space-y-4">
        {editing && (
          <EditDocument
            document={doc}
            suggestedTopics={(filters.data?.tags ?? []).map((t) => t.tag)}
            onClose={() => setEditing(false)}
          />
        )}

      {/* Why a document is not answerable yet, in the one place someone would
          go looking. */}
      {doc.status === "failed" && doc.pipelineError && (
        <div className="rounded-card border border-danger/40 bg-danger-bg p-4 text-sm text-danger-text">
          <p className="font-medium">This document could not be processed.</p>
          <p className="mt-1 font-mono text-xs text-danger-text/80">{doc.pipelineError}</p>
        </div>
      )}
      {doc.status === "processing" && (
        <div className="rounded-card border border-amber/40 bg-amber-bg p-4 text-sm text-amber-text">
          This document is still being processed and will not appear in answers until it finishes.
        </div>
      )}

      <div className="rounded-card border border-border bg-card shadow-panel">
        <Tabs defaultValue="exec">
          <div className="flex items-center justify-between border-b border-border px-4 pt-3">
            <TabsList className="border-b-0 px-0">
              <TabsTrigger value="exec">Executive Summary</TabsTrigger>
              <TabsTrigger value="detailed">Detailed Summary</TabsTrigger>
              <TabsTrigger value="entities">Extracted Entities</TabsTrigger>
            </TabsList>
          </div>

          {/* Summaries and entities are produced by the document processor, so
              they are rendered defensively — an older processor, or one that
              failed partway, simply has not written them. */}
          <TabsContent value="exec" className="space-y-3">
            {exec?.structured ? (
              <>
                {exec.structured.lead && (
                  <p className="text-sm leading-relaxed">{exec.structured.lead}</p>
                )}
                {exec.structured.bullets.length > 0 && (
                  <>
                    <p className="mt-3 text-sm font-medium">Key findings and implications:</p>
                    <ul className="list-disc space-y-1.5 pl-5 text-sm">
                      {exec.structured.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </>
                )}
                {exec.structured.implication && (
                  <p className="mt-3 text-sm leading-relaxed">
                    <span className="font-medium">Implication: </span>
                    {exec.structured.implication}
                  </p>
                )}
              </>
            ) : exec?.prose ? (
              <Prose text={exec.prose} />
            ) : (
              <p className="py-4 text-sm text-muted-foreground">
                No executive summary was produced for this document.
              </p>
            )}
          </TabsContent>

          <TabsContent value="detailed" className="space-y-5">
            {detailed?.sections ? (
              detailed.sections.map((section, i) => (
                <div key={i}>
                  <h3 className="erika-serif mb-1.5 text-lg">{section.heading}</h3>
                  {section.body && <p className="text-sm leading-relaxed">{section.body}</p>}
                  {section.bullets && (
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      {section.bullets.map((b, j) => (
                        <li key={j}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            ) : detailed?.prose ? (
              <Prose text={detailed.prose} />
            ) : (
              <p className="py-4 text-sm text-muted-foreground">
                No detailed summary was produced for this document.
              </p>
            )}
          </TabsContent>

          {/* Five boxes, not three. People and Locations were extracted all
              along and never rendered — and People is how ERIKA's users
              actually browsed: "what do we have that mentions this provost". */}
          <TabsContent value="entities" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <EntityBox title="Topics" items={details.entities.topics} />
            <EntityBox title="Keywords" items={details.entities.keywords} />
            <EntityBox title="Organizations & Groups" items={details.entities.organizations} />
            <EntityBox title="People" items={details.entities.people ?? []} />
            <EntityBox title="Locations" items={details.entities.locations ?? []} />
          </TabsContent>
        </Tabs>
      </div>

      <div className="rounded-card border border-border bg-card p-4 shadow-panel">
        {/* The two-column caption is a table header for the rows below it, so
            it stays put while they scroll past. It scrolls with the page —
            this box used to be a 560px window inside a page that also
            scrolled, which is the arrangement where a wheel does one of two
            things depending on where the pointer is. */}
        <div className="sticky top-0 z-10 -mx-4 mb-3 flex gap-4 border-b border-border bg-card px-4 pb-2 text-xs text-muted-foreground">
          <div className="flex-1">Machine-readable text</div>
          <div className="flex-1">
            PDF Page
            {pdf.isLoading && <span className="ml-2 text-muted-foreground/70">Loading PDF…</span>}
            {/* Why there is no PDF, once, where it applies to every page below:
                "no Google Drive connection" and "not shared with the Box app"
                are different fixes, and the tiles alone cannot say which. */}
            {pdf.isError && !pages.some((p) => p.imageUrl) && (
              <span className="ml-2 text-muted-foreground/70">
                {pdf.error instanceof ApiError ? pdf.error.message : "The PDF could not be loaded."}
              </span>
            )}
          </div>
        </div>

        {pages.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No page text has been ingested for this document yet.
          </div>
        ) : (
          <div className="space-y-4">
            {pages.map((p) => (
              <div
                key={p.page}
                ref={(el) => {
                  pageRefs.current[p.page] = el;
                }}
                className={
                  // scroll-mt clears the sticky caption, so a linked page lands
                  // with its heading showing.
                  "grid scroll-mt-12 items-start gap-4 rounded-md sm:grid-cols-2 " +
                  (targetPage === p.page ? "ring-2 ring-accent ring-offset-2 ring-offset-card" : "")
                }
              >
                <div className="rounded-md border border-input bg-secondary p-3">
                  <div className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground/70">
                    Page {p.page}
                  </div>
                  <Markdown>{p.parsed}</Markdown>
                </div>

                <div className="relative overflow-hidden rounded-md border border-input bg-muted">
                  {pdf.data ? (
                    <PdfPage pdf={pdf.data} page={p.page} defaultAspect={pdfAspect} />
                  ) : p.imageUrl && !pdf.isLoading ? (
                    // No PDF (Box off, no link, or not a PDF): the page image
                    // the processor rasterised at ingest is the same page.
                    <img
                      src={p.imageUrl}
                      alt={`Page ${p.page}`}
                      loading="lazy"
                      className="block w-full bg-white object-contain"
                    />
                  ) : pdf.isLoading ? (
                    <div className="aspect-[8.5/11] w-full animate-pulse bg-muted/60" aria-hidden />
                  ) : (
                    // Compact, not page-sized: when there is no PDF at all this
                    // repeats on every page, and a letter-sized empty box per page
                    // would triple the length of the list for nothing.
                    <div className="flex min-h-[8rem] w-full flex-col items-center justify-center gap-1.5 p-4 text-center text-xs text-muted-foreground">
                      <span className="text-xl">▤</span>
                      <span>No PDF is available for page {p.page}</span>
                    </div>
                  )}
                  {internal && <InternalOnly />}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </PageScroll>
    </Page>
  );
}

/**
 * The marker on every PDF page of an internal document.
 *
 * On the page itself rather than once in the header, because the page is what
 * gets screenshotted and pasted into a client email — a warning a scroll away
 * from the thing being copied is not a warning. It does not block the page:
 * `pointer-events-none`, and it sits in the corner rather than across the text.
 */
function InternalOnly() {
  return (
    <div
      className="pointer-events-none absolute right-2 top-2 z-[1] flex items-center gap-1.5 rounded-md border border-amber/60 bg-amber-bg/95 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-text shadow-sm"
      role="note"
      title="Internal document: do not share directly with the client."
    >
      <ShieldAlert size={13} aria-hidden />
      Internal Only
    </div>
  );
}
